import LinearProgress from '@mui/material/LinearProgress'
import { ArrowRightIcon } from '@mui/x-date-pickers'
import BoxMobileComponent from 'components/box-mobile'
import ButtonMobileComponent from 'components/button-mobile'
import { AssignmentIndIcon, ManageNotificationsIcon } from 'components/icons'
import { CarpoolDashboardIcon } from 'components/icons/CarpoolDashboardIcon'
import { CarpoolManagementIcon } from 'components/icons/CarpoolManagementIcon'
import { CommunityImpactIcon } from 'components/icons/CommunityImpactIcon'
import { DismissalCheckInIcon } from 'components/icons/DismissalCheckin'
import { MessagingFilledIcon } from 'components/icons/MessagingFilledIcon'
import { PlusIcon } from 'components/icons/PlusIcon'
import SchoolLogo from 'components/logo-school'
import Menu, { MenuProps } from 'components/menu'
import WizardComponent from 'components/wizard'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'
import { useEffect, useRef, useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { CommunityType } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UserNotificationOutputProps, UserOutputProps } from 'types/UserTypes'
import {
  formatLabel,
  isWeb,
  LABELS,
  MAX_ALLOWED_REGISTRATIONS_PER_USER,
  titleCaseSchoolNameForUI,
} from 'utils/constants'
import { Drawer } from 'vaul'
import './index.css'

const maxStudentsOnScreen: Record<DeviceSize, number> = {
  xs: 3,
  sm: MAX_ALLOWED_REGISTRATIONS_PER_USER,
  md: MAX_ALLOWED_REGISTRATIONS_PER_USER,
  lg: MAX_ALLOWED_REGISTRATIONS_PER_USER,
  xl: MAX_ALLOWED_REGISTRATIONS_PER_USER,
}

const HUB_BOX_STYLES = {
  carpoolDashboard: {
    bgColor: '#FEF9C3',
    icon: <CarpoolDashboardIcon size={32} />,
    iconColor: '#CA8A04',
  },
  carpoolManagement: {
    bgColor: '#FEF1DD',
    icon: <CarpoolManagementIcon size={32} />,
    iconColor: '#F59E42',
  },
  carpoolTracking: {
    bgColor: '#E0F2FE',
    icon: <DismissalCheckInIcon size={32} />,
    iconColor: '#0369A1',
  },
  communityImpact: {
    bgColor: '#CCFBF1',
    icon: <CommunityImpactIcon size={32} />,
    iconColor: '#0D9488',
  },
  groupChat: {
    bgColor: '#DCFCE7',
    icon: <MessagingFilledIcon fill='#FFFFFF' size={32} />,
    iconColor: '#166534',
  },
  notification: {
    bgColor: '#E0E7FF',
    icon: <ManageNotificationsIcon size={32} />,
    iconColor: '#166534',
  },
}

const HubComponent: React.FC<{
  studentId?: string
  userMenu: MenuProps
  userStudents: StudentProps[]
}> = ({ studentId: studentIdFromQuery, userMenu, userStudents }) => {
  // 1. State Hooks
  const deviceSize = useDeviceSize()
  const navigate: NavigateFunction = useNavigate()
  const [selectedStudentId, setSelectedStudentId] = useState<string>()
  const [showAllStudents, setShowAllStudents] = useState<boolean>()
  // State for toggling view of all students

  // 2. Derived Constants
  const { unreadMessageCount, unreadNotificationCount } = userMenu

  // 3. Effect Hooks
  // Effect to set initial states and handle student selection changes
  useEffect(() => {
    setShowAllStudents(false)
    setSelectedStudentId(studentIdFromQuery)
  }, [studentIdFromQuery])

  // 4. Derived State, Validations and Early Returns
  const studentCount = userStudents.length
  const selectedStudent: StudentProps | undefined = userStudents.find((s) => s.studentId === selectedStudentId)
  const otherStudentIds = userStudents.filter((s) => s.studentId !== selectedStudentId).map((s) => s.studentId)
  const visibleStudentCount = showAllStudents
    ? otherStudentIds.length
    : Math.min(maxStudentsOnScreen[deviceSize] - 1, otherStudentIds.length)
  const hiddenStudentCount = otherStudentIds.length - visibleStudentCount

  const {
    schoolName,
    isCarpoolManagementActive = false,
    isDismissalManagementActive = false,
    isSchoolSubscription: isSchoolSubscriptionActive = false,
    isStudentSubscription: isStudentSubscriptionActive = false,
  } = selectedStudent ?? {}
  const isSubscriptionActive: boolean = isSchoolSubscriptionActive || isStudentSubscriptionActive
  const comType: CommunityType = CommunityType.SCHOOL

  // Messages
  const disabledByAdminMessage: string = `Disabled by your ${LABELS[comType]?.lowercasePossessive ?? formatLabel('community', 'lowercasePossessive')} admin`
  const requiresCommunitySubscriptionMessage: string = `Requires a ${LABELS[comType]?.lowercase ?? formatLabel('community', 'lowercase')} subscription`
  const requiresSubscriptionMessage: string = `Requires a subscription`

  // Scenario 1: Carpool request only needs the carpool feature
  const isCarpoolRequestEnabled: boolean = isCarpoolManagementActive
  const requiresCarpoolFeature = (msg: string): string => {
    if (!isCarpoolManagementActive) return disabledByAdminMessage
    return msg
  }

  // Scenario 2: Carpool management and tracking needs either carpool (with any subscription) OR dismissal (with school subscription)
  const isCarpoolManagementEnabled: boolean =
    (isCarpoolManagementActive && isSubscriptionActive) || (isDismissalManagementActive && isSchoolSubscriptionActive)
  const isCarpoolTrackingEnabled: boolean = isCarpoolManagementEnabled
  const requiresCarpoolOrDismissalFeatureWithSubscription = (msg: string): string => {
    if (!isCarpoolManagementActive && !isDismissalManagementActive) return disabledByAdminMessage
    if (isCarpoolManagementActive && !isSubscriptionActive) return requiresSubscriptionMessage
    if (isDismissalManagementActive && !isSchoolSubscriptionActive) return requiresCommunitySubscriptionMessage
    return msg
  }

  // 5. Event Handlers

  // 6. Render
  const body: JSX.Element = (
    <div className={isWeb ? 'mt-0' : 'mx-auto'}>
      {schoolName ? (
        <div className={`flex items-center ${showAllStudents ? 'gap-2' : 'justify-between'} py-2 mb-1`}>
          {/* --- LEFT SIDE: Welcome to School Message for Selected Student --- */}
          {/* Added min-w-0 to allow the child text to truncate properly */}
          <div className='flex gap-2 items-center min-w-0'>
            <div className='flex-shrink-0 p-1 rounded-full ring-2 ring-yellow-500 ring-offset-2 shadow-sm'>
              <SchoolLogo schoolName={schoolName} className='w-10 h-10 rounded-full object-cover' />
            </div>
            {!showAllStudents && (
              <div className='min-w-0'>
                {studentCount > 1 ? (
                  // If there are multiple students, show the student number
                  <button
                    onClick={() => {
                      const currentIndex = userStudents.findIndex((s) => s.studentId === selectedStudentId)
                      const nextIndex = (currentIndex + 1) % userStudents.length
                      navigate(`/hub?id=${userStudents[nextIndex].studentId}`)
                    }}
                    className='flex items-center gap-1 hover:opacity-80 transition-opacity'
                  >
                    <p className='text-sm text-disable'>
                      {`Student ${userStudents.findIndex((s) => s.studentId === selectedStudentId) + 1} of ${userStudents.length}`}
                    </p>
                    <ArrowRightIcon className='text-disable' />
                  </button>
                ) : (
                  // Otherwise, show the standard "Welcome to" message
                  <p className='text-sm text-disable'>Welcome to</p>
                )}
                <p className='font-semibold text-headings text-lg truncate'>{titleCaseSchoolNameForUI(schoolName)}</p>
              </div>
            )}
          </div>

          {/* --- RIGHT SIDE: School Avatars for Student Selection --- */}
          <div className='flex gap-1 items-center shrink-0'>
            {otherStudentIds.slice(0, visibleStudentCount).map((studentId) => (
              <button
                key={studentId}
                onClick={() => navigate(`/hub?id=${studentId}`)}
                className={
                  'h-10 w-10 p-1 bg-transparent rounded-full transition-all duration-200 ease-in-out border-2 border-gray-200 ' +
                  (isWeb
                    ? 'hover:ring-2 hover:ring-yellow-500 hover:ring-offset-2 focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 hover:border-transparent'
                    : '')
                }
              >
                <SchoolLogo
                  schoolName={userStudents.find((s: StudentProps) => s.studentId === studentId)?.schoolName ?? ''}
                />
              </button>
            ))}

            {/* View More/Less Button */}
            {(showAllStudents || hiddenStudentCount > 0) && (
              <button
                onClick={() => setShowAllStudents((prev) => !prev)}
                className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600 border-2 border-white cursor-pointer hover:bg-gray-300'
              >
                {showAllStudents ? 'Less' : `+${hiddenStudentCount}`}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center p-8 text-center'>
          <AssignmentIndIcon size={64} color='gray' />
          <h2 className='text-2xl font-semibold mb-2'>No Students Registered</h2>
          <p className='text-gray-600 mb-6'>Get started by registering your first student to access all features.</p>
          <ButtonMobileComponent
            text='Register First Student'
            data_testid='register-first-student-testid'
            width={'auto'}
            href='/add'
            icon={<PlusIcon size={24} />}
          />
        </div>
      )}

      {studentCount > 0 && (
        <div>
          {/* Carpool Requests and Management */}
          <div className={`flex gap-2 pb-3 ${isWeb ? 'w-full' : ''} items-stretch`}>
            {/* Carpool Dashboard */}
            <BoxMobileComponent
              name='Find Carpool Matches'
              tagline={requiresCarpoolFeature('Map families on your route')}
              {...HUB_BOX_STYLES.carpoolDashboard}
              className='w-full h-full'
              data_testid='carpool-dashboard-box-testid'
              to={`/carpool/dashboard?id=${selectedStudentId}`}
              disabled={!isCarpoolRequestEnabled}
            />
            {/* Carpool Management */}
            <BoxMobileComponent
              name='Assign Drivers'
              tagline={requiresCarpoolOrDismissalFeatureWithSubscription('Schedule carpools for the school year')}
              {...HUB_BOX_STYLES.carpoolManagement}
              className='w-full h-full'
              data_testid='arrival-pickup-box-testid'
              to={`/carpool/management?id=${selectedStudentId}`}
              disabled={!isCarpoolManagementEnabled}
            />
          </div>

          {/* Carpool Tracking and Community Impact */}
          <div className={`flex gap-2 pb-3 ${isWeb ? 'w-full' : ''} items-stretch`}>
            {/* Carpool Tracking */}
            <BoxMobileComponent
              name="Track Today's Pickup"
              tagline={requiresCarpoolOrDismissalFeatureWithSubscription('View driver location and check-in')}
              {...HUB_BOX_STYLES.carpoolTracking}
              className='w-full h-full'
              data_testid='carpool-tracking-box-testid'
              to={!isCarpoolTrackingEnabled ? '#' : `/carpool/tracking?id=${selectedStudentId}`}
              disabled={!isCarpoolTrackingEnabled}
            />
            {/* Community Impact */}
            <BoxMobileComponent
              name='Community Impact'
              tagline={`Celebrate your collective efforts!`}
              {...HUB_BOX_STYLES.communityImpact}
              className='w-full h-full'
              data_testid='community-impact-box-testid'
              to={`/impact?id=${selectedStudentId}`}
            />
          </div>

          {/* Chat and Notification */}
          <div className={`flex gap-2 pb-3 ${isWeb ? 'w-full' : ''} items-stretch`}>
            {/* Group Chat */}
            <BoxMobileComponent
              label={unreadMessageCount > 0 ? `${unreadMessageCount}` : ''}
              name='Community Chat'
              tagline='Connect with other parents'
              {...HUB_BOX_STYLES.groupChat}
              className='w-full h-full'
              data_testid='group-chat-box-testid'
              to='/messaging'
            />
            {/* Notification */}
            <BoxMobileComponent
              label={unreadNotificationCount > 0 ? `${unreadNotificationCount}` : ''}
              name='Notifications'
              tagline='Get smart reminders and carpool updates'
              {...HUB_BOX_STYLES.notification}
              className='w-full h-full'
              data_testid='notification-box-testid'
              to='/notifications'
            />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      {/* Mobile */}
      {!isWeb && (
        <div className='flex flex-col h-screen'>
          <Menu {...userMenu} />
          <div data-vaul-drawer-wrapper=''>
            <Drawer.Root activeSnapPoint={selectedStudent ? 0.15 : 0.95} open={true} modal={false}>
              <Drawer.Portal>
                <Drawer.Overlay className='fixed inset-0 bg-black/40' />
                <Drawer.Content className='bg-zinc-100 flex flex-col rounded-t-[40px] fixed bottom-0 left-0 right-0 focus:outline-none'>
                  <div className='font-figtree px-5 font-medium pt-4 overflow-y-auto max-h-[calc(100vh-80px)] pb-6'>
                    {body}
                  </div>
                </Drawer.Content>
              </Drawer.Portal>
            </Drawer.Root>
          </div>
        </div>
      )}
      {/* Web */}
      {isWeb && (
        <div className='pt-12'>
          <WizardComponent title={''}>{body}</WizardComponent>
        </div>
      )}
    </>
  )
}

function Index({
  user,
  userPhoto,
  userNotifications,
  students,
  processQuery,
  onMount,
}: {
  user?: UserOutputProps
  userPhoto: string
  userNotifications: UserNotificationOutputProps[]
  students: StudentProps[]
  processQuery: (id: string) => string
  onMount?: () => Promise<void>
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  // Fetch data on first mount of this component using onMount callback
  // Why? Using onUpdate callback the various components update the root state
  // However, if server state changes, the client does not update unless the app is reloaded
  // So we fetch data on first mount of this homepage hub component to ensure we have the latest data
  const hasMounted = useRef(false)
  useEffect(() => {
    const initialize = async () => {
      if (onMount && !hasMounted.current) {
        hasMounted.current = true
        await onMount()
      }
    }
    initialize()
  }, [onMount])

  // If we  navigate to this URL without a student id in query search parameters,
  // use our root app handler callback to find the student to display
  const studentId = searchParams.get('id') ?? ''
  useEffect(() => {
    setSearchParams({ id: processQuery(studentId) })
  }, [studentId, processQuery, setSearchParams])
  if (!user) return <LinearProgress />

  // Build props for child component
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)
  if (!user || (!student && studentId && students && students?.length > 0)) return <LinearProgress />

  const userMenu: MenuProps = {
    phoneNumber: user.phoneNumber,
    name: user.name ?? '',
    studentCount: students && students?.length > 0 ? students.length : 0,
    unreadMessageCount: user.userUnreadMessageCount,
    unreadNotificationCount: userNotifications.filter((v) => v.notificationIsUnread === true).length,
    relationship: student?.userRelationship,
    photoLink: userPhoto,
  }
  return <HubComponent studentId={studentId} userMenu={userMenu} userStudents={students} />
}

export default Index
